import { FC, useEffect, useRef } from "react";
import { useAppStateContext } from "../../../middleware/context/state-context-provider";
import { StandardHandler } from "../../../middleware/handlers/standard-handler";

/**
 * Fetches an IFC already sitting in storage and runs it through the same
 * conversion an uploaded file gets, so the two paths cannot drift.
 *
 * modelFileData must be a Blob: ConvertIfcToFragments treats it as a File and
 * calls arrayBuffer() on it, and its own try/catch would swallow the TypeError
 * from anything else into a bare "failed to prepare".
 */
const PrepareStoredModel = async (openModel: any) => {
  const response = await fetch(openModel["modelFileUrl"]);

  if (!response.ok) {
    throw new Error(`Failed to download the model: ${response.status}`);
  }

  return StandardHandler.ConvertIfcToFragments({
    modelName: openModel["modelName"],
    modelFileData: await response.blob(),
  });
};

export const StandardViewport: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const viewportContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewportContainer = viewportContainerRef.current;
    if (!viewportContainer || !state.user) return;

    // Started directly rather than through a dispatch. dispatch only queues the
    // action, so the reducer would not have created the viewer yet by the time
    // the effect below runs.
    StandardHandler.Start([viewportContainer, dispatch]);

    return () => {
      dispatch({ type: "CLOSE_STANDARD" });
    };
  }, []);

  useEffect(() => {
    // Converting a large model runs for minutes. If the user opens a different
    // one meanwhile this result is stale, and acting on it would load the
    // abandoned model over the one they are now looking at.
    let abandoned = false;

    if (state.openModel !== undefined && state.openModel !== null) {
      const openModel = state.openModel;

      if ("modelFragmentsUrl" in openModel) {
        dispatch({ type: "OPEN_FRAGMENTS" });
      } else if ("modelFileUrl" in openModel) {
        // A model that reached storage without fragments -- a forge transfer,
        // which cannot build them because the conversion is web-ifc WASM and
        // runs only here. Convert once, store the buffer, and every later open
        // takes the fast path above.
        dispatch({ type: "RESPONSE", response: "PREPARING" });

        PrepareStoredModel(openModel)
          .then((modelFragments: any) => {
            if (abandoned) {
              return;
            }
            if (!modelFragments) {
              dispatch({
                type: "RESPONSE",
                response: "Failed to prepare the model. Please try again.",
              });
              return;
            }

            dispatch({
              type: "STORE_MODEL_FRAGMENTS",
              payload: {
                email: state.user?.email!,
                projectID: state.user?.openProject?.projectID!,
                fileID: openModel["fileID"],
                modelName: modelFragments.modelName,
                modelFragmentsData: modelFragments.modelFragmentsData,
                modelFragmentsSize: modelFragments.modelFragmentsSize,
                type: "ModelFragmentsInterface",
              },
            });
          })
          .catch((error: any) => {
            if (abandoned) {
              return;
            }
            console.error("Error preparing stored model:", error);
            dispatch({
              type: "RESPONSE",
              response: "Failed to prepare the model. Please try again.",
            });
          });
      } else if ("modelFileData" in state.openModel) {
        // Parsing the IFC and exporting the fragments buffer is client-side
        // work that can run for minutes on a large model, so it gets its own
        // status rather than sharing the upload dialog.
        dispatch({ type: "RESPONSE", response: "PREPARING" });

        StandardHandler.ConvertIfcToFragments(state.openModel)
          .then((modelFragments: any) => {
            if (!modelFragments) {
              dispatch({
                type: "RESPONSE",
                response: "Failed to prepare the model. Please try again.",
              });
              return;
            }

            dispatch({ type: "CONVERT_MODEL", payload: modelFragments });
          })
          .catch((error: any) => {
            console.error("Error converting IFC to fragments:", error);
            dispatch({
              type: "RESPONSE",
              response: "Failed to prepare the model. Please try again.",
            });
          });
      }
    }

    return () => {
      abandoned = true;
    };
  }, [state.openModel]);

  return <div id="standardViewport" ref={viewportContainerRef}></div>;
};
